import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Broadcast, ChartLineUp, ShieldCheck, Trophy, Lightning, Users, TrendUp, TrendDown } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import TradingViewWidget, { TVEconomicCalendar, TVMarketOverview } from '@/components/app/TradingView';

export default function Dashboard() {
  const { user } = useAuth();
  const [signals, setSignals] = useState([]);
  const [stats, setStats] = useState({});
  const [news, setNews] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, st, n] = await Promise.all([api.get('/signals?limit=5'), api.get('/signals/stats'), api.get('/news')]);
        setSignals(s.data); setStats(st.data); setNews(n.data.slice(0, 4));
      } catch {}
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="data-label text-saffron">Welcome back</div>
          <h1 className="font-heading text-3xl font-bold mt-1">{user?.full_name}</h1>
        </div>
        <div className="flex items-center gap-2 glass px-4 py-2 rounded-lg">
          <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          <span className="text-sm">Markets Open · Live</span>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Signals', value: stats.total ?? 0, icon: Broadcast, color: 'text-saffron' },
          { label: 'Open Signals', value: stats.open ?? 0, icon: Lightning, color: 'text-blue-400' },
          { label: 'Win Rate', value: (stats.win_rate ?? 0) + '%', icon: Trophy, color: 'text-green-400' },
          { label: 'Closed Wins', value: stats.wins ?? 0, icon: TrendUp, color: 'text-green-400' },
        ].map(({label, value, icon: I, color}) => (
          <div key={label} className="glass p-5 rounded-lg neon-border">
            <div className="flex items-start justify-between">
              <div>
                <div className="data-label">{label}</div>
                <div className={`stat-num text-3xl mt-2 ${color}`}>{value}</div>
              </div>
              <I size={24} className={color} weight="duotone" />
            </div>
          </div>
        ))}
      </div>

      {/* Verification banner */}
      {user?.verification_status === 'not_submitted' && !user?.is_premium && (
        <div className="glass-strong p-6 rounded-xl neon-border flex items-center justify-between flex-wrap gap-4 border-saffron/40" data-testid="verification-banner">
          <div className="flex items-center gap-4">
            <ShieldCheck size={40} className="text-saffron" weight="duotone" />
            <div>
              <div className="font-heading font-bold text-lg">Unlock Premium — Free</div>
              <div className="text-sm text-muted-foreground">Verify a partner broker account and get instant premium access.</div>
            </div>
          </div>
          <Link to="/verification"><Button data-testid="cta-verify" className="bg-saffron hover:bg-saffron-hover text-white shadow-glow-saffron">Verify Broker</Button></Link>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Chart */}
        <div className="lg:col-span-2 glass rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-lg">BTC/USDT · Live</h3>
            <Link to="/charts" className="text-xs text-saffron hover:underline">All Charts →</Link>
          </div>
          <TradingViewWidget symbol="BINANCE:BTCUSDT" height={380} />
        </div>

        {/* Recent Signals */}
        <div className="glass rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-lg">Latest Signals</h3>
            <Link to="/signals" className="text-xs text-saffron hover:underline">View all →</Link>
          </div>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {signals.length === 0 && <div className="text-sm text-muted-foreground p-3">No signals yet.</div>}
            {signals.map(s => (
              <div key={s.id} className="p-3 rounded-md bg-surface-base border border-white/5 hover:border-saffron/30 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="font-mono font-bold">{s.asset}</div>
                  <span className={`text-xs px-2 py-0.5 rounded font-semibold ${s.direction === 'BUY' ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}`}>{s.direction}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1 font-mono">
                  {s.locked ? 'Premium locked 🔒' : `Entry ${s.entry} · SL ${s.stop_loss}`}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="glass rounded-xl p-4">
          <h3 className="font-heading font-bold text-lg mb-3">Economic Calendar</h3>
          <TVEconomicCalendar />
        </div>
        <div className="glass rounded-xl p-4">
          <h3 className="font-heading font-bold text-lg mb-3">Market Overview</h3>
          <TVMarketOverview />
        </div>
      </div>

      <div className="glass rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold text-lg">Latest News</h3>
          <Link to="/news" className="text-xs text-saffron hover:underline">All news →</Link>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {news.map(n => (
            <div key={n.id} className="p-4 rounded-md bg-surface-base border border-white/5 hover:border-saffron/30 transition-colors">
              <div className="data-label text-saffron">{n.category}</div>
              <div className="font-heading font-semibold mt-1">{n.title}</div>
              <div className="text-xs text-muted-foreground mt-1">{n.summary}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
